import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, ShieldCheck, Lightning, Sparkle } from "@phosphor-icons/react";

/**
 * Premium SaaS checkout — final step of registration.
 *
 * Reads sessionStorage.agentus_checkout_intent (set by Register or by Login
 * when 402 fires) and POSTs /api/payments/subscribe to receive a Stripe
 * Checkout URL, then redirects.
 *
 * v0.96.17 — 2-plan selector. "essential" (R$ 299 · 5k créd) ou "base"
 * (Enterprise, R$ 799 · 18k créd). Default = "base" (mantém pixel-perfect do
 * fluxo antigo se cliente não interagir com o seletor).
 */
export default function Checkout() {
  const nav = useNavigate();
  const [plans, setPlans] = useState(null);   // [essential, base]
  const [selected, setSelected] = useState("base");
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("agentus_checkout_intent");
    if (!raw) {
      toast.error("Sessão de pagamento expirada. Refaça o cadastro.");
      nav("/cadastro");
      return;
    }
    setIntent(JSON.parse(raw));
    const url = new URL(window.location.href);
    if (url.searchParams.get("cancelled")) setCancelled(true);
    // Pre-select via query string (?plan=essential|base) — útil pro site
    // marketing linkar direto pro Essencial.
    const planParam = url.searchParams.get("plan");
    if (planParam === "essential" || planParam === "base") {
      setSelected(planParam);
    }
    api.get("/payments/plans").then(({ data }) => {
      setPlans(data.subscriptions || [data.subscription]);
    });
  }, [nav]);

  const startCheckout = async () => {
    if (!intent || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/subscribe", {
        registration_token: intent.registration_token,
        origin_url: window.location.origin,
        plan_code: selected,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não consegui iniciar o pagamento. Tente novamente.");
      setLoading(false);
    }
  };

  if (!plans || !intent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-5 h-5 rounded-full border-2 border-slate-200 border-t-slate-900 animate-spin" />
      </div>
    );
  }

  const current = plans.find((p) => p.code === selected) || plans[0];
  const essential = plans.find((p) => p.code === "essential");
  const base = plans.find((p) => p.code === "base");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4 sm:p-8" data-testid="checkout-page">
      <div className="w-full max-w-3xl">
        <div className="mb-6 flex items-center justify-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-slate-500">
          <span className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold text-[11px]">1</span>
          <span className="text-slate-900 font-semibold">Conta</span>
          <span className="w-8 h-px bg-slate-300" />
          <span className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold text-[11px]">2</span>
          <span className="text-slate-900 font-semibold">Plano</span>
          <span className="w-8 h-px bg-slate-300" />
          <span className="w-7 h-7 rounded-full bg-white border border-slate-300 text-slate-400 flex items-center justify-center font-semibold text-[11px]">3</span>
          <span className="text-slate-400">Acesso</span>
        </div>

        {cancelled && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[13px] p-3" data-testid="checkout-cancelled-banner">
            Pagamento cancelado. Você pode tentar novamente abaixo.
          </div>
        )}

        <div className="mb-5 text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-1">
            Escolha o plano
          </div>
          <h1 className="text-2xl sm:text-[26px] font-semibold tracking-tight text-slate-900">
            Qual é o tamanho da sua operação?
          </h1>
        </div>

        {/* ── 2 plan cards side by side ── */}
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
          {[essential, base].filter(Boolean).map((p) => {
            const isSelected = selected === p.code;
            const isBig = p.code === "base";
            return (
              <button
                key={p.code}
                type="button"
                onClick={() => setSelected(p.code)}
                data-testid={`plan-card-${p.code}`}
                className={`text-left bg-white rounded-2xl border-2 transition-all p-5 sm:p-6 relative overflow-hidden ${
                  isSelected
                    ? "border-slate-900 shadow-xl shadow-slate-200/80"
                    : "border-slate-200 hover:border-slate-400"
                }`}
              >
                {isBig && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] uppercase tracking-wider font-semibold">
                    <Sparkle size={9} weight="fill" />
                    Mais escolhido
                  </div>
                )}

                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    isSelected ? "border-slate-900 bg-slate-900" : "border-slate-300"
                  } flex items-center justify-center`}>
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    {p.code === "essential" ? "Plano menor" : "Plano completo"}
                  </span>
                </div>

                <h3 className="text-[18px] font-semibold tracking-tight text-slate-900 mb-1">
                  {p.label}
                </h3>
                <p className="text-[11.5px] text-slate-500 mb-4 leading-snug min-h-[28px]">
                  {p.target}
                </p>

                <div className="flex items-end gap-1 mb-1">
                  <span className="text-[32px] font-semibold tracking-tighter text-slate-900 leading-none">
                    R$ {Math.floor(p.price_brl)}
                  </span>
                  <span className="text-[13px] text-slate-500 mb-1">,00/mês</span>
                </div>
                <div className="text-[11px] text-slate-500 mb-4">
                  {p.monthly_credits.toLocaleString("pt-BR")} créditos/mês
                </div>

                <ul className="space-y-1.5">
                  {p.features.slice(0, 4).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[12px] text-slate-700 leading-snug">
                      <CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* ── CTA bar ── */}
        <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-200/80 p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:justify-between">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-400 mb-0.5">
                Você selecionou
              </div>
              <div className="text-[15px] font-semibold text-slate-900">
                {current.label}
                <span className="text-slate-500 font-normal ml-2">
                  · R$ {Math.floor(current.price_brl)},00/mês
                </span>
              </div>
            </div>
            <button
              onClick={startCheckout}
              disabled={loading}
              data-testid="checkout-start-btn"
              className="bg-slate-900 text-white text-[14px] font-medium px-6 py-3 rounded-xl hover:bg-slate-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
            >
              {loading ? (
                <>
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white/25 border-t-white animate-spin" />
                  Abrindo pagamento seguro…
                </>
              ) : (
                <>
                  <Lightning size={15} weight="fill" />
                  Continuar para o pagamento
                  <ArrowRight size={14} weight="bold" />
                </>
              )}
            </button>
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-3 gap-2 text-center text-[10.5px] text-slate-500">
            <div className="flex flex-col items-center gap-1">
              <ShieldCheck size={14} weight="fill" className="text-slate-600" />
              <span>Pagamento via Stripe</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <CheckCircle size={14} weight="fill" className="text-slate-600" />
              <span>Cancele quando quiser</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Lightning size={14} weight="fill" className="text-slate-600" />
              <span>Acesso imediato</span>
            </div>
          </div>
        </div>

        <p className="text-center text-[12px] text-slate-500 mt-5">
          Sua conta está aguardando pagamento como <strong className="text-slate-700">{intent.email}</strong>.
        </p>
      </div>
    </div>
  );
}
